import { Cell } from '@/grid/Cell';
import { EntrancePoint } from './EntrancePoint';
import { Cluster } from './Cluster';

export class AbstractNode {
  readonly id: number;
  readonly entrance: EntrancePoint;
  readonly position: Cell;
  edges: AbstractEdge[] = [];

  gCost: number = Infinity;
  hCost: number = 0;
  fCost: number = Infinity;
  parent: AbstractNode | null = null;

  constructor(id: number, entrance: EntrancePoint) {
    this.id = id;
    this.entrance = entrance;
    this.position = entrance.getCenter();
  }

  reset(): void {
    this.gCost = Infinity;
    this.hCost = 0;
    this.fCost = Infinity;
    this.parent = null;
  }

  equals(other: AbstractNode): boolean {
    return this.id === other.id;
  }
}

export class AbstractEdge {
  readonly from: AbstractNode;
  readonly to: AbstractNode;
  readonly cost: number;
  readonly path: Cell[];

  constructor(
    from: AbstractNode,
    to: AbstractNode,
    cost: number,
    path: Cell[] = []
  ) {
    this.from = from;
    this.to = to;
    this.cost = cost;
    this.path = path;
  }
}

export class AbstractGraph {
  private nodes: Map<number, AbstractNode> = new Map();
  private edges: AbstractEdge[] = [];
  private nextNodeId: number = 0;

  addNode(entrance: EntrancePoint): AbstractNode {
    const node = new AbstractNode(this.nextNodeId++, entrance);
    this.nodes.set(node.id, node);
    return node;
  }

  addEdge(from: AbstractNode, to: AbstractNode, cost: number, path: Cell[] = []): void {
    const edge = new AbstractEdge(from, to, cost, path);
    this.edges.push(edge);
    from.edges.push(edge);
  }

  getNode(entranceId: number): AbstractNode | null {
    for (const node of this.nodes.values()) {
      if (node.entrance.id === entranceId) {
        return node;
      }
    }
    return null;
  }

  getNodesInCluster(cluster: Cluster): AbstractNode[] {
    const result: AbstractNode[] = [];
    for (const node of this.nodes.values()) {
      if (node.entrance.connects(cluster)) {
        result.push(node);
      }
    }
    return result;
  }

  getAllNodes(): AbstractNode[] {
    return Array.from(this.nodes.values());
  }

  getAllEdges(): AbstractEdge[] {
    return this.edges;
  }

  reset(): void {
    for (const node of this.nodes.values()) {
      node.reset();
    }
  }
}
